<template>
  <div
    v-if="showResumeApp"
    ref="banner"
    class="sticky-shadow z-30 overflow-hidden sm:sticky sm:top-[--siteHeaderHeight]"
    :style="{
      maxHeight: shouldShow ? '200px' : '0px',
      transition: 'max-height 1000ms ease-out'
    }"
  >
    <aside class="relative border-b-4 border-sky-800 bg-[#f6f1fe] p-3.5">
      <div
        class="mx-auto flex max-w-fit flex-col gap-3 sm:px-5 md:max-w-6xl md:flex-row md:items-center md:justify-between"
      >
        <div>
          <h2 class="pr-3 text-xl">
            <span class="hidden sm:inline md:hidden lg:inline">
              You started {{ article }} {{ currentMapdApp?.brandName }} Application
              <span class="celebrate">🎉</span>
            </span>
            <span class="inline xs:whitespace-nowrap sm:hidden md:inline lg:hidden">
              Your {{ currentMapdApp?.brandName }} Application <span class="celebrate">🎉</span>
            </span>
          </h2>
          <ul class="flex flex-wrap items-center gap-x-3">
            <li
              v-for="item in listItems"
              :key="item.icon"
              class="flex items-center leading-normal tracking-tight text-gray-800 last:hidden even:max-[830px]:hidden sm:last:flex"
            >
              <HpIcon :name="item.icon" stroke="2.5" class="text-success" />
              {{ item.text }}
            </li>
          </ul>
        </div>

        <div class="flex items-center gap-2">
          <HpButton variant="secondary" class="w-fit !px-3.5" @click="handlePlanDetailsClick">
            <span class="whitespace-nowrap">Plan Details</span>
          </HpButton>
          <div @click="handleApplyClick" class="min-w-0 max-w-72">
            <PxApplyButton
              :medicare-id="currentMapdApp?.medicareId ?? ''"
              :plan-year="currentMapdApp?.planYear ?? 0"
              :type="currentMapdApp?.planType ?? PlanType.MAPD"
              :externalAppUrl="null"
              :carrierName="currentMapdApp?.carrierName ?? ''"
            >
              <span class="hidden items-center whitespace-nowrap pb-0.5 lg:flex">
                Continue My Application
              </span>
              <span class="flex whitespace-nowrap pb-0.5 lg:hidden">Continue Application</span>
              <HpIcon name="arrow-right" class="ml-2 hidden xs:flex" />
            </PxApplyButton>
          </div>
        </div>
      </div>

      <HpButton
        variant="minimal"
        class="absolute right-1 top-1 aspect-square min-h-0 w-fit !p-1 !text-gray-600 hover:bg-primary-300 hover:!text-gray-700"
        @click="isVisible = false"
      >
        <HpIcon name="lucide:x" class="size-5" />
        <span class="sr-only">Close this message.</span>
      </HpButton>
    </aside>
  </div>
</template>

<script lang="ts" setup>
  import { useElementSize } from '@vueuse/core'
  import { ApplicationStore } from '~/stores/applications'
  import { PlanType } from '~/generated/api-clients-generated'

  const banner = ref<HTMLElement | null>(null)
  const { height: bannerHeight } = useElementSize(banner)
  watchEffect(() => ResumeBannerOffset.set(bannerHeight.value))
  onUnmounted(() => ResumeBannerOffset.set(0))

  const appStore = ApplicationStore.use()
  const { push } = AppRouter.use()
  const { path } = AppRoute.use()
  const { track } = Analytics.use()
  const { getComponentContent } = Cms.use()

  const { content } = await getComponentContent('ResumeApplicationBanner')

  const article = computed(() =>
    /^[aeiou]/i.test(currentMapdApp.value?.brandName ?? '') ? 'an' : 'a'
  )

  const currentMapdApp = computed(() => {
    const started = appStore.applications?.filter(
      (a) => a.status === 'started' && a.planType === PlanType.MAPD
    )
    if (!started?.length) return undefined
    return started.reduce((latest, a) => {
      if (!latest.updatedAt) return a
      if (!a.updatedAt) return latest
      return a.updatedAt > latest.updatedAt ? a : latest
    })
  })

  const showResumeApp = computed(
    () =>
      !!currentMapdApp.value &&
      (path.value.includes('/quotes') ||
        path.value.includes('/details') ||
        path.value.includes('/compare'))
  )

  const listItems = computed(() =>
    content.value?.data?.listItems?.map((item: { icon: string; text: string }) => ({
      ...item,
      text: item.text.replaceAll('{carrier}', currentMapdApp.value?.brandName ?? '')
    }))
  )

  const isVisible = ref(false)
  let revealBannerTimer: ReturnType<typeof setTimeout> | null = null

  const shouldShow = computed(() => !!(showResumeApp.value && isVisible.value))

  const handleApplyClick = () => {
    track('ResumeAppBannerApplyClicked', {
      clickedFrom: path.value,
      medicareId: currentMapdApp.value?.medicareId
    })
  }

  const handlePlanDetailsClick = () => {
    track('ResumeAppBannerDetailsClicked', {
      clickedFrom: path.value
    })
    push(`/details/${currentMapdApp.value?.medicareId}`)
  }

  watch(
    () => showResumeApp.value,
    (val) => {
      if (val && !isVisible.value) {
        const initialPage = path.value
        revealBannerTimer = setTimeout(() => {
          isVisible.value = true
          track('ResumeAppBannerShown', {
            initialPageViewed: initialPage
          })
        }, 1000)
      } else if (!val) {
        if (revealBannerTimer) clearTimeout(revealBannerTimer)
        isVisible.value = false
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    if (revealBannerTimer) clearTimeout(revealBannerTimer)
  })
</script>

<style scoped>
  .sticky-shadow {
    box-shadow: 0 6px 15px -8px rgba(0 0 0 / 25%);
  }

  .celebrate {
    @apply ml-1 inline-block scale-0;
    animation: pop-in 200ms cubic-bezier(0, 0, 0, 2) 1.75s forwards;
  }

  @keyframes pop-in {
    100% {
      @apply scale-90;
    }
  }
</style>
